<template>
  <Doughnut
    :data="finalChartData"
    :options="finalChartOptions"
    :plugins="plugins"
  />
</template>

<script lang="ts">
import 'chart.js/auto'
import { Doughnut } from 'vue-chartjs'
import DataLabelsPlugin from 'chartjs-plugin-datalabels'
import type {
  ChartOptions,
  ChartData as FinalChartData,
  LayoutPosition,
  Plugin
} from 'chart.js/auto'
import { legendSpacing } from '~/assets/chart/plugins'
import { formatMemberValue } from '~/services/analyticsFormat'
import { FormatContext } from '~/types/analyticsFormat'
import type { BaseDataSet, TrendChartData } from '~/types/analytics'
import {
  getColorPalette,
  processLargeDataTrim,
  removeZeroInData
} from '~/services/chart'
import exportedColors from '~/assets/style/_exportDefaultChartColors.module.scss'
import {
  DATALABELS_PLUGIN_OPTIONS,
  DEFAULT_CHART_OPTIONS,
  SCALES_OPTIONS,
  TOOLTIP_PLUGIN_OPTIONS
} from '~/constants/chart'

const LEGEND_INCREASE_WIDTH = 56
const LEGEND_INCREASE_HEIGHT = 8
const LEGEND_INCREASE_MARGIN_TOP = 16

const LARGE_DATA_TRIM_LIMIT = 7

export default {
  name: 'TrendDistribution',
  components: { Doughnut },
  props: {
    legendPosition: {
      type: String as PropType<LayoutPosition>,
      required: true
    },
    chartData: {
      type: Object as PropType<TrendChartData>,
      required: true
    }
  },
  computed: {
    plugins() {
      const LegendSpacingPlugin = legendSpacing<'doughnut'>(
        this.legendPosition === 'right' ? LEGEND_INCREASE_WIDTH : 0,
        this.legendPosition === 'bottom' ? LEGEND_INCREASE_HEIGHT : 0,
        0,
        this.legendPosition === 'bottom' ? LEGEND_INCREASE_MARGIN_TOP : 0
      )
      return [LegendSpacingPlugin, DataLabelsPlugin as Plugin<'doughnut'>]
    },
    finalChartOptions(): ChartOptions<'doughnut'> {
      return {
        ...DEFAULT_CHART_OPTIONS,
        scales: {
          y: {
            ...SCALES_OPTIONS.y,
            ticks: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: this.legendPosition,
            align: this.legendPosition === 'bottom' ? 'start' : 'center',
            maxHeight: this.legendPosition === 'bottom' ? 120 : undefined,
            reverse: false,
            labels: {
              font: {
                size: 13
              },
              pointStyle: 'rectRounded',
              usePointStyle: true
            }
          },
          tooltip: {
            ...TOOLTIP_PLUGIN_OPTIONS,
            enabled: true,
            callbacks: {
              label: context => {
                const label = context.label
                const format = (context.dataset as BaseDataSet).format
                const value = context.parsed
                return `${label}: ${formatMemberValue(
                  value,
                  format,
                  FormatContext.CHART,
                  false
                )}`
              }
            }
          },
          datalabels: {
            ...DATALABELS_PLUGIN_OPTIONS,
            color: '#FFFFFF',
            formatter: (value, context) => {
              const format = (context.dataset as BaseDataSet).format
              return formatMemberValue(
                value,
                format,
                FormatContext.CHART,
                false
              )
            }
          }
        }
      }
    },
    finalChartData(): FinalChartData<'doughnut'> {
      const datasetSize = this.chartData.datasets[0]!.data.filter(
        value => value !== null
      ).length

      const colors = getColorPalette(datasetSize, Object.values(exportedColors))

      const finalChartData = processLargeDataTrim(
        removeZeroInData(this.chartData),
        LARGE_DATA_TRIM_LIMIT
      )

      return {
        ...finalChartData,
        datasets: finalChartData.datasets.map(dataset => {
          return {
            ...dataset,
            data: dataset.data as number[],
            stack: undefined,
            backgroundColor: colors,
            borderWidth: 0,
            borderColor: colors,
            hoverBorderWidth: 0
          }
        })
      }
    }
  }
}
</script>
